<script setup lang="ts">
  import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
  import { useSettingsStore } from '@stores/settings';
  import { useDepositStore } from '@stores/deposit';
  import { useMenuStore } from '@stores/menu';
  import { useAuthStore } from '@stores/auth';

  const settingsStore = useSettingsStore();
  const depositStore = useDepositStore();
  const menuStore = useMenuStore();
  const runtimeConfig = useRuntimeConfig();
  const authStore = useAuthStore();

  const sitesJivo = [
    'https://backoffice.lendarias.com',
    'https://app.betizze.com',
    'https://app.bet3as.com'
  ];

  const openJivo = () => {
    jivo_api.open();
  };

  const sitesWp = [
    {
      api: 'https://app.pixpremioesporte.io',
      wp: 'https://wa.me/5534996588281'
    }
  ];

  const handleClick = computed(() => {
    if (settingsStore.settings.general_live_chat !== '123456') {
      return menuStore.chat;
    } else if (sitesJivo.includes(runtimeConfig.public.API_URL)) {
      return openJivo;
    } else if (sitesWp.filter((site) => site.api === runtimeConfig.public.API_URL)) {
      const matchedSite = sitesWp.find((site) => site.api === runtimeConfig.public.API_URL);
      if (matchedSite) {
        return () => window.open(matchedSite.wp, '_blank');
      }
    } else {
      return '';
    }
  });
</script>

<template>
  <Menu as="div" class="relative z-[51] inline-block text-left">
    <MenuButton class="flex items-center justify-center">
      <BaseButton size="xs" class="!border-gray-700 md:hidden">
        <Icon name="ic:baseline-person" class="my-auto" />
      </BaseButton>

      <BaseButton size="sm" class="hidden !border-gray-700 md:flex">
        <Icon name="ic:baseline-person" class="my-auto" />
      </BaseButton>
    </MenuButton>

    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <MenuItems
        class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-slate-700 rounded-md bg-brand-background border border-brand-separator shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      >
        <div class="px-1 py-1">
          <MenuItem v-slot="{ active, close }">
            <nuxt-link to="/user/account">
              <MenuDropdownItem
                @click="close"
                :class="[
                  active
                    ? 'hover:bg-brand-card hover:text-brand-primary transition'
                    : 'text-gray-300',
                  'group flex w-full items-center rounded-md px-2 py-2 text-sm'
                ]"
              >
                <Icon name="ic:baseline-person" />
                Conta
              </MenuDropdownItem>
            </nuxt-link>
          </MenuItem>
          <MenuItem v-slot="{ active, close }" v-if="settingsStore.settings.refer_enabled">
            <nuxt-link to="/user/indications">
              <MenuDropdownItem
                @click="close"
                :class="[
                  active
                    ? 'hover:bg-brand-card hover:text-brand-primary transition'
                    : 'text-gray-300',
                  'group flex w-full items-center rounded-md px-2 py-2 text-sm'
                ]"
              >
                <Icon name="gridicons:multiple-users" />
                Indicações
              </MenuDropdownItem>
            </nuxt-link>
          </MenuItem>

          <MenuItem v-if="settingsStore.settings.deposit_enable_popup" v-slot="{ active, close }">
            <MenuDropdownItem
              @click="(depositStore.toggleModal(), close)"
              :class="[
                active
                  ? 'hover:bg-brand-card hover:text-brand-primary transition'
                  : 'text-gray-300',
                'group flex w-full items-center rounded-md px-2 py-2 text-sm'
              ]"
            >
              <Icon name="fa6-solid:arrows-down-to-line" />
              Depositar
            </MenuDropdownItem>
          </MenuItem>

          <MenuItem v-else v-slot="{ active, close }">
            <nuxt-link to="/user/deposit">
              <MenuDropdownItem
                @click="close"
                :class="[
                  active
                    ? 'hover:bg-brand-card hover:text-brand-primary transition'
                    : 'text-gray-300',
                  'group flex w-full items-center rounded-md px-2 py-2 text-sm'
                ]"
              >
                <Icon name="fa6-solid:arrows-down-to-line" />
                Depositar
              </MenuDropdownItem>
            </nuxt-link>
          </MenuItem>

          <MenuItem v-slot="{ active, close }">
            <nuxt-link to="/user/wallet/withdraw">
              <MenuDropdownItem
                @click="close"
                :class="[
                  active
                    ? 'hover:bg-brand-card hover:text-brand-primary transition'
                    : 'text-gray-300',
                  'group flex w-full items-center rounded-md px-2 py-2 text-sm'
                ]"
              >
                <Icon name="fa6-solid:arrows-up-to-line" />
                Saque
              </MenuDropdownItem>
            </nuxt-link>
          </MenuItem>
          <MenuItem v-slot="{ active, close }">
            <nuxt-link to="/user/wallet">
              <MenuDropdownItem
                @click="close"
                :class="[
                  active
                    ? 'hover:bg-brand-card hover:text-brand-primary transition'
                    : 'text-gray-300',
                  'group flex w-full items-center rounded-md px-2 py-2 text-sm'
                ]"
              >
                <Icon name="tdesign:chart-analytics" />
                Transações
              </MenuDropdownItem>
            </nuxt-link>
          </MenuItem>

          <MenuItem v-slot="{ active, close }">
            <nuxt-link to="/user/coupon">
              <MenuDropdownItem
                @click="close"
                :class="[
                  active
                    ? 'hover:bg-brand-card hover:text-brand-primary transition'
                    : 'text-gray-300',
                  'group flex w-full items-center rounded-md px-2 py-2 text-sm'
                ]"
              >
                <Icon name="icon-park-solid:ticket" />
                Cupons
              </MenuDropdownItem>
            </nuxt-link>
          </MenuItem>

          <MenuItem v-slot="{ active, close }">
            <nuxt-link to="/user/bonus">
              <MenuDropdownItem
                @click="close"
                :class="[
                  active
                    ? 'hover:bg-brand-card hover:text-brand-primary transition'
                    : 'text-gray-300',
                  'group flex w-full items-center rounded-md px-2 py-2 text-sm'
                ]"
              >
                <Icon name="fa6-solid:gift" />
                Bônus
              </MenuDropdownItem>
            </nuxt-link>
          </MenuItem>

          <MenuItem v-slot="{ active, close }">
            <nuxt-link to="/user/account/password">
              <MenuDropdownItem
                @click="close"
                :class="[
                  active
                    ? 'hover:bg-brand-card hover:text-brand-primary transition'
                    : 'text-gray-300',
                  'group flex w-full items-center rounded-md px-2 py-2 text-sm'
                ]"
              >
                <Icon name="fluent:key-reset-24-filled" />
                Senha
              </MenuDropdownItem>
            </nuxt-link>
          </MenuItem>
          <MenuItem v-slot="{ active, close }">
            <nuxt-link to="/user/protection">
              <MenuDropdownItem
                @click="close"
                :class="[
                  active
                    ? 'hover:bg-brand-card hover:text-brand-primary transition'
                    : 'text-gray-300',
                  'group flex w-full items-center rounded-md px-2 py-2 text-sm'
                ]"
              >
                <Icon name="fa6-solid:shield-heart" />
                Proteção do jogador
              </MenuDropdownItem>
            </nuxt-link>
          </MenuItem>
          <MenuItem v-slot="{ active }" @click="() => handleClick" v-if="settingsStore.settings.general_show_chat_user_menu">
            <button
              :class="[
                active
                  ? 'hover:bg-brand-card hover:text-brand-primary transition'
                  : 'text-gray-300',
                'group flex w-full items-center rounded-md px-2 py-2 text-sm font-semibold font-sans gap-4'
              ]"
            >
              <Icon name="tdesign:service-filled" />
              Suporte ao vivo
            </button>
          </MenuItem>
        </div>

        <div class="px-1 py-1">
          <MenuItem v-slot="{ active }">
            <button
              @click="authStore.setLoggedIn(false)"
              :class="[
                active ? 'hover:bg-darken-danger hover:text-danger transition' : 'text-gray-300',
                'group flex w-full items-center rounded-md px-2 py-2 text-sm font-semibold font-sans gap-4'
              ]"
            >
              <Icon name="streamline:emergency-exit-solid" />
              Sair
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>
